/**
 * Command-palette search: menus + API users/establishments with RBAC gating.
 */

import * as React from 'react';
import { useInterfaceElementsStore } from '@/stores/interfaceElementsStore';
import { searchService } from '@/services/search.service';
import { useDynamicSearchMenus } from '@/hooks/useDynamicSearchMenus';
import { GlobalSearchResponse, MenuSearchResult, SearchResultItem } from '@/types/search.types';
import { getErrorMessage } from '@/utils/errors-utils';
import {
  SEARCH_DEBOUNCE_MS,
  SEARCH_MIN_QUERY_LENGTH,
  SEARCH_PAGE_SIZE,
} from '@/components/search/search-configs';
import {
  filterSearchMenus,
  mapSearchDataResults,
  mapSearchMenuResults,
  paginateSearchResults,
} from '@/components/search/search-utils';

/** Command-palette search state and result lists. */
interface UseGlobalSearchReturn {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  isLoadingData: boolean;
  hasApiResponded: boolean;
  dataResults: GlobalSearchResponse['data'] | null;
  menuResults: MenuSearchResult[];
  allResults: SearchResultItem[];
  allDataResults: SearchResultItem[];
  allMenuResultsMapped: SearchResultItem[];
  paginatedMenuResults: SearchResultItem[];
  totalDataCount: number;
  hasDataResults: boolean;
  hasMenuResults: boolean;
  hasAnyResults: boolean;
  searchError: string | null;
  showDropdown: boolean;
  setShowDropdown: (show: boolean) => void;
  menuPage: number;
  setMenuPage: (page: number) => void;
  dataPage: number;
  setDataPage: (page: number) => void;
  clearSearch: () => void;
}

/** Command-palette search: local menus plus API users/establishments, gated by sidebar RBAC. */
export const useGlobalSearch = (): UseGlobalSearchReturn => {
  const dynamicMenus = useDynamicSearchMenus();
  const [searchQuery, setSearchQuery] = React.useState('');
  const [isLoadingData, setIsLoadingData] = React.useState(false);
  const [hasApiResponded, setHasApiResponded] = React.useState(false);
  const [searchError, setSearchError] = React.useState<string | null>(null);
  const [dataResults, setDataResults] = React.useState<GlobalSearchResponse['data'] | null>(null);
  const [totalDataCount, setTotalDataCount] = React.useState(0);
  const [menuResults, setMenuResults] = React.useState<MenuSearchResult[]>([]);
  const [showDropdown, setShowDropdown] = React.useState(false);
  const [menuPage, setMenuPage] = React.useState(1);
  const [dataPage, setDataPage] = React.useState(1);
  const debounceTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const filterMenusLocally = React.useCallback(
    (query: string): MenuSearchResult[] => {
      return filterSearchMenus(
        dynamicMenus,
        query,
        useInterfaceElementsStore.getState().elementsByInterface,
      );
    },
    [dynamicMenus],
  );

  const fetchDataFromApi = async (query: string) => {
    setIsLoadingData(true);
    setHasApiResponded(false);
    setSearchError(null);
    try {
      const response = await searchService.globalSearch(query, dataPage, SEARCH_PAGE_SIZE);
      setDataResults(response.data);
      setTotalDataCount(response.totalResults ?? 0);
      setHasApiResponded(true);
    } catch (error) {
      setDataResults(null);
      setTotalDataCount(0);
      setHasApiResponded(true);
      setSearchError(getErrorMessage(error));
    } finally {
      setIsLoadingData(false);
    }
  };

  React.useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const filteredMenus = filterMenusLocally(searchQuery);
    setMenuResults(filteredMenus);

    if (searchQuery.length >= SEARCH_MIN_QUERY_LENGTH) {
      setShowDropdown(true);
      debounceTimerRef.current = setTimeout(() => {
        void fetchDataFromApi(searchQuery);
      }, SEARCH_DEBOUNCE_MS);
    } else if (searchQuery.length > 0) {
      setShowDropdown(true);
      setDataResults(null);
      setTotalDataCount(0);
      setIsLoadingData(false);
      setHasApiResponded(false);
      setSearchError(null);
    } else {
      setShowDropdown(false);
      setDataResults(null);
      setTotalDataCount(0);
      setIsLoadingData(false);
      setHasApiResponded(false);
      setSearchError(null);
      setMenuResults([]);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery, dataPage]);

  React.useEffect(() => {
    setDataPage(1);
  }, [searchQuery]);

  const allDataResults = React.useMemo(() => mapSearchDataResults(dataResults), [dataResults]);

  const allMenuResultsMapped = React.useMemo(
    () => mapSearchMenuResults(menuResults),
    [menuResults],
  );
  const allResults = React.useMemo(
    () => [...allDataResults, ...allMenuResultsMapped],
    [allDataResults, allMenuResultsMapped],
  );

  const hasDataResults = allDataResults.length > 0;
  const hasMenuResults = allMenuResultsMapped.length > 0;
  const hasAnyResults = hasDataResults || hasMenuResults;

  const paginatedMenuResults = React.useMemo(
    () => paginateSearchResults(allMenuResultsMapped, menuPage),
    [allMenuResultsMapped, menuPage],
  );

  const clearSearch = () => {
    setSearchQuery('');
    setDataResults(null);
    setTotalDataCount(0);
    setMenuResults([]);
    setShowDropdown(false);
    setMenuPage(1);
    setDataPage(1);
    setHasApiResponded(false);
    setSearchError(null);
  };

  return {
    searchQuery,
    setSearchQuery,
    isLoadingData,
    hasApiResponded,
    searchError,
    dataResults,
    menuResults,
    allResults,
    allDataResults,
    allMenuResultsMapped,
    paginatedMenuResults,
    totalDataCount,
    hasDataResults,
    hasMenuResults,
    hasAnyResults,
    showDropdown,
    setShowDropdown,
    menuPage,
    setMenuPage,
    dataPage,
    setDataPage,
    clearSearch,
  };
};
